import type { LucideIcon } from 'lucide-react';

import { QuestionType } from '@repo/types/enums/questionType';
import { CopyCheckIcon, HandshakeIcon, MicIcon, RectangleEllipsisIcon, SquareIcon, SquareStackIcon, TextCursorInputIcon } from 'lucide-react';

/**
 * Represents the content displayed for a question type.
 * Includes label, descriptions, and an associated icon.
 */
interface QuestionLabelContent {
  /** Display label for the question type (e.g., "Jednokrotny wybór") */
  label: string;
  /** Description shown to end users when answering the question */
  desc: string;
  /** Description shown in the question builder interface */
  builderDesc: string;
  /** Lucide icon component for visual representation */
  icon: LucideIcon;
}

/** Type mapping each QuestionType to its corresponding label content */
type QuestionsLabel = Record<QuestionType, QuestionLabelContent>;

/**
 * Configuration object containing labels and descriptions for all question types.
 * Maps each question type to its display information and icon.
 */
export const questionsLabel: QuestionsLabel = {
  [QuestionType.SINGLE]: {
    label: 'Jednokrotny wybór',
    desc: 'Wybierz jedną poprawną odpowiedź spośród dostępnych opcji.',
    builderDesc: 'Dodaj pytanie z jedną poprawną odpowiedzią.',
    icon: SquareIcon,
  },
  [QuestionType.MULTIPLE]: {
    label: 'Wielokrotny wybór',
    desc: 'Wybierz dowolną liczbę poprawnych odpowiedzi z listy.',
    builderDesc: 'Dodaj pytanie z wieloma poprawnymi odpowiedziami.',
    icon: SquareStackIcon,
  },
  [QuestionType.FILL]: {
    label: 'Wypełnij lukę',
    desc: 'Uzupełnij zdanie, przeciągając odpowiednie bloki tekstowe w puste miejsca.',
    builderDesc: 'Dodaj pytanie z lukami do uzupełnienia.',
    icon: RectangleEllipsisIcon,
  },
  [QuestionType.VOICE]: {
    label: 'Odpowiedź ustna',
    desc: 'Nagraj swoją odpowiedź głosową na zadane pytanie.',
    builderDesc: 'Dodaj pytanie wymagające odpowiedzi głosowej.',
    icon: MicIcon,
  },
  [QuestionType.MATCHING]: {
    label: 'Dopasowywanie',
    desc: 'Połącz elementy z dwóch list, aby utworzyć poprawne pary.',
    builderDesc: 'Dodaj pytanie polegające na dopasowywaniu elementów.',
    icon: HandshakeIcon,
  },
  [QuestionType.LONG]: {
    label: 'Odpowiedź pisemna',
    desc: 'Napisz odpowiedź na zadane pytanie.',
    builderDesc: 'Dodaj pytanie wymagające dłuższej odpowiedzi pisemnej.',
    icon: TextCursorInputIcon,
  },
  [QuestionType.TRANSLATE]: {
    label: 'Układanie zdań',
    desc: 'Ułóż poprawne zdania, wybierając odpowiednie wyrażenia z podanej listy.',
    builderDesc: 'Dodaj pytanie polegające na układaniu zdań z podanych wyrażeń.',
    icon: CopyCheckIcon,
  },
};
